import '../App.css'
import logo from '../PB001b_ComedySeen_090723_logo_2.jpg'
import {
    Center, Image, Text, Container, Space, SimpleGrid, Divider,
    Card, Badge, Stack, Group, createStyles, rem, Box, Anchor
} from '@mantine/core'
import Footer from '../components/Footer';
import Head from '../components/Head'
import { IconGlobe, IconBuildingSkyscraper, IconMicrophone2, IconUsers, IconHelpCircle, IconTrophy } from '@tabler/icons-react'

// ── Colour palette ────────────────────────────────────────────────────────────
const PURPLE = '#532d86'
const TEAL = '#2b9eb3'
const GREEN = '#1a9e2e'

// ── Styles ────────────────────────────────────────────────────────────────────
const useStyles = createStyles((theme) => ({

    heroWrapper: {
        background: 'radial-gradient(ellipse at 50% 60%, #ede0ff 0%, #e8f6fb 50%, #f4fbff 100%)',
        paddingTop: rem(24),
        paddingBottom: rem(36),
        textAlign: 'center',
    },

    pageTitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 800,
        letterSpacing: '-0.02em',
        fontSize: rem(60),
        color: PURPLE,
        lineHeight: 1.1,

        [theme.fn.smallerThan('md')]: { fontSize: rem(44) },
        [theme.fn.smallerThan('sm')]: { fontSize: rem(32) },
    },

    sectionTitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(32),
        textAlign: 'center',
        color: theme.colors.gray[8],

        [theme.fn.smallerThan('sm')]: { fontSize: rem(24) },
    },

    dividerLabel: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(12),
        letterSpacing: '0.08em',
        textTransform: 'uppercase' as const,
        color: theme.colors.gray[5],
    },

    sectionDivider: {
        margin: `${rem(32)} 0 ${rem(24)}`,
    },

    // ── Prize cards ───────────────────────────────────────────────────────────
    prizeCard: {
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        background: 'rgba(255,255,255,0.8)',
        backdropFilter: 'blur(4px)',

        '&:hover': {
            boxShadow: theme.shadows.md,
            transform: 'translateY(-2px)',
        },
    },

    regionHeader: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(16),
        letterSpacing: '0.04em',
    },

    categoryLabel: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 600,
        fontSize: rem(13),
        letterSpacing: '0.06em',
        textTransform: 'uppercase' as const,
        color: theme.colors.gray[6],
        marginBottom: rem(8),
    },

    // ── Podium prize rows ──────────────────────────────────────────────────────
    podiumRow: {
        display: 'flex',
        alignItems: 'center',
        gap: rem(10),
        padding: `${rem(6)} ${rem(8)}`,
        borderRadius: rem(8),
        marginBottom: rem(4),
    },

    prizeAmount: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 800,
        fontSize: rem(20),

        [theme.fn.smallerThan('sm')]: { fontSize: rem(16) },
    },

    // ── Points grid ───────────────────────────────────────────────────────────
    pointsCard: {
        background: 'rgba(255,255,255,0.8)',
        border: `1px solid ${theme.colors.gray[2]}`,
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        height: '100%',

        '&:hover': {
            boxShadow: theme.shadows.sm,
            transform: 'translateY(-2px)',
        },
    },

    pointsCardTitle: {
        fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
        fontWeight: 700,
        fontSize: rem(15),
        textAlign: 'center',
        marginBottom: rem(12),
    },

    pointRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: rem(8),
        padding: `${rem(4)} 0`,
        borderBottom: `1px solid ${theme.colors.gray[1]}`,
        '&:last-of-type': { borderBottom: 'none' },
    },

    pointBadge: {
        minWidth: rem(42),
        textAlign: 'center' as const,
        flexShrink: 0,
        fontFamily: "'Outfit', sans-serif",
        fontWeight: 700,
    },
}))

// ── Helpers ───────────────────────────────────────────────────────────────────
function SectionDivider({ label }: { label: string }) {
    const { classes } = useStyles()
    return (
        <Divider
            className={classes.sectionDivider}
            label={<span className={classes.dividerLabel}>{label}</span>}
            labelPosition="center"
            size="xs"
        />
    )
}

const MEDALS = ['🥇', '🥈', '🥉']
const MEDAL_COLORS = ['#c8960c', '#8a9aaa', '#c47a3a']

interface PrizeEntry {
    label: string
    prizes: string[]
}

function PrizeTier({ label, prizes }: PrizeEntry) {
    const { classes } = useStyles()
    return (
        <Box mb="md">
            <Text className={classes.categoryLabel}>{label}</Text>
            {prizes.map((prize, i) => (
                <div key={i} className={classes.podiumRow}
                    style={{ background: i === 0 ? 'rgba(200,150,12,0.08)' : i === 1 ? 'rgba(138,154,170,0.08)' : 'rgba(196,122,58,0.08)' }}
                >
                    <Text sx={{ fontSize: rem(20), lineHeight: 1 }}>{MEDALS[i]}</Text>
                    <Text className={classes.prizeAmount} sx={{ color: MEDAL_COLORS[i] }}>
                        {prize.match(/^\$/) ? prize : ''}
                    </Text>
                    <Text size="sm" color="dimmed" sx={{ flex: 1 }}>
                        {prize.match(/^\$/) ? (i === 0 ? '1st place' : i === 1 ? '2nd place' : '3rd place') : prize}
                    </Text>
                </div>
            ))}
        </Box>
    )
}

interface PointRowProps {
    value: string
    description: string
    color?: string
}
function PointRow({ value, description, color = PURPLE }: PointRowProps) {
    const { classes } = useStyles()
    return (
        <div className={classes.pointRow}>
            <Badge className={classes.pointBadge} color="violet" variant="light" size="md">{value}</Badge>
            <Text size="sm" color="dimmed">{description}</Text>
        </div>
    )
}

// ── Main component ────────────────────────────────────────────────────────────
function LeaderboardPrizes26() {
    const { classes } = useStyles()

    const links = [
        { "link": "/About", "label": "About" },
        { "link": "/Support", "label": "Support" },
        { "link": "/Pp", "label": "Privacy" },
        { "link": "/Tos", "label": "Terms of Use" },
    ]

    return (
        <body>
            <Head />

            {/* ── Hero ── */}
            <div className={classes.heroWrapper}>
                <Group position="center" spacing="xs" mb={rem(10)}>
                    <IconTrophy size={28} color="#c8960c" />
                    <IconTrophy size={44} color="#c8960c" />
                    <IconTrophy size={28} color="#c8960c" />
                </Group>
                <Text className={classes.pageTitle}>2026 Leaderboard Prizes</Text>
                <Text size="lg" color="dimmed" mt={6}>
                    Recognising the best comics, fans, and venues in the scene
                </Text>
            </div>

            {/* ═══════════════════════════════════════
                COMEDIANS
            ════════════════════════════════════════ */}
            <SectionDivider label="Comedians" />

            <Container size="lg">
                <Group mb="md" spacing="xs" position="center">
                    <IconMicrophone2 size={22} color={PURPLE} />
                    <Text className={classes.sectionTitle}>Comedians</Text>
                </Group>

                <SimpleGrid
                    cols={2}
                    spacing="lg"
                    breakpoints={[{ maxWidth: '48rem', cols: 1, spacing: 'md' }]}
                >
                    {/* ── Global ── */}
                    <Stack spacing="lg">
                        <Card
                            className={classes.prizeCard}
                            padding="xl"
                            radius="lg"
                            withBorder
                            shadow="sm"
                            sx={{ borderLeft: `4px solid ${PURPLE}` }}
                        >
                            <Group spacing="xs" mb="lg">
                                <IconGlobe size={20} color={PURPLE} />
                                <Text className={classes.regionHeader} sx={{ color: PURPLE }}>Global</Text>
                            </Group>

                            <PrizeTier
                                label="Top Comic"
                                prizes={['$500', '$200', '$100']}
                            />
                            <PrizeTier
                                label="Hardest Working Comic"
                                prizes={['$100']}
                            />
                            <PrizeTier
                                label="Top Open Mic"
                                prizes={['$100', '$50', '$25']}
                            />

                            <Divider my="md" />
                            <Center>
                                <Stack spacing={2} align="center">
                                    <Text size="xs" color="dimmed">Presented by</Text>
                                    <Anchor href="https://www.properformancewheels.com" target="_blank" rel="noopener noreferrer">
                                        <Image
                                            src="https://res.cloudinary.com/hwldcqspa/image/upload/v1787094565/for_url/db131b9a-9b59-11f1-ad31-42004e494300_Straight_Logo_with_Tagline.png"
                                            maw={140}
                                            fit="contain"
                                            alt="Pro Performance Wheels"
                                        />
                                    </Anchor>
                                </Stack>
                            </Center>
                        </Card>

                        {/* Top Producer */}
                        <Card
                            className={classes.prizeCard}
                            padding="xl"
                            radius="lg"
                            withBorder
                            shadow="sm"
                            sx={{ borderLeft: `4px solid ${PURPLE}` }}
                        >
                            <Group spacing="xs" mb="md">
                                <IconGlobe size={20} color={PURPLE} />
                                <Text className={classes.regionHeader} sx={{ color: PURPLE }}>Global - Other Prizes</Text>
                            </Group>

                            <PrizeTier
                                label="Top Producer"
                                prizes={[
                                    'Comedy Seen covers half the marketing spend on 2 shows',
                                    'Comedy Seen covers half the marketing spend on 1 show',
                                    'Comedy Seen covers half the marketing spend on 1 show',
                                ]}
                            />
                        </Card>
                    </Stack>

                    {/* ── Regional ── */}
                    <Stack spacing="lg">
                        {/* Brooklyn */}
                        <Card
                            className={classes.prizeCard}
                            padding="lg"
                            radius="lg"
                            withBorder
                            shadow="sm"
                            sx={{ borderLeft: `4px solid ${TEAL}` }}
                        >
                            <Group spacing="xs" mb="md">
                                <IconBuildingSkyscraper size={20} color={TEAL} />
                                <Text className={classes.regionHeader} sx={{ color: TEAL }}>Brooklyn</Text>
                            </Group>
                            <PrizeTier label="Top Comic" prizes={['$100', '$50', '$25']} />
                            <Divider my="sm" />
                            <Center>
                                <Stack spacing={2} align="center">
                                    <Text size="xs" color="dimmed">Presented by</Text>
                                    <Image src={logo} maw={70} fit="contain" />
                                </Stack>
                            </Center>
                        </Card>

                        {/* Jacksonville */}
                        <Card
                            className={classes.prizeCard}
                            padding="lg"
                            radius="lg"
                            withBorder
                            shadow="sm"
                            sx={{ borderLeft: `4px solid ${TEAL}` }}
                        >
                            <Group spacing="xs" mb="md">
                                <IconBuildingSkyscraper size={20} color={TEAL} />
                                <Text className={classes.regionHeader} sx={{ color: TEAL }}>Jacksonville</Text>
                            </Group>
                            <PrizeTier label="Top Comic" prizes={['$100', '$50', '$25']} />
                            <Divider my="sm" />
                            <Center>
                                <Stack spacing={2} align="center">
                                    <Text size="xs" color="dimmed">Presented by</Text>
                                    <Image src={logo} maw={70} fit="contain" />
                                </Stack>
                            </Center>
                        </Card>

                        {/* Miami */}
                        <Card
                            className={classes.prizeCard}
                            padding="lg"
                            radius="lg"
                            withBorder
                            shadow="sm"
                            sx={{ borderLeft: `4px solid ${TEAL}` }}
                        >
                            <Group spacing="xs" mb="md">
                                <IconBuildingSkyscraper size={20} color={TEAL} />
                                <Text className={classes.regionHeader} sx={{ color: TEAL }}>Miami</Text>
                            </Group>
                            <PrizeTier label="Top Comic" prizes={[
                                '2 sets filmed and edited + headshot session',
                                '1 set filmed and edited + headshot session',
                                '1 set filmed and edited or headshot session',
                            ]} />
                            <Divider my="sm" />
                            <Center>
                                <Stack spacing={2} align="center">
                                    <Text size="xs" color="dimmed">Presented by</Text>
                                    <Image src={logo} maw={70} fit="contain" />
                                </Stack>
                            </Center>
                        </Card>
                    </Stack>
                </SimpleGrid>
            </Container>

            {/* ═══════════════════════════════════════
                FANS
            ════════════════════════════════════════ */}
            <SectionDivider label="Fans" />

            <Container size="sm">
                <Group mb="md" spacing="xs" position="center">
                    <IconUsers size={22} color={GREEN} />
                    <Text className={classes.sectionTitle}>Fans</Text>
                </Group>

                <Card
                    className={classes.prizeCard}
                    padding="xl"
                    radius="lg"
                    withBorder
                    shadow="sm"
                    sx={{ borderLeft: `4px solid ${GREEN}` }}
                >
                    <Group spacing="xs" mb="md">
                        <IconGlobe size={20} color={GREEN} />
                        <Text className={classes.regionHeader} sx={{ color: GREEN }}>Global · Top Fan</Text>
                    </Group>

                    <div className={classes.podiumRow} style={{ background: 'rgba(26,158,46,0.07)', flexWrap: 'wrap' }}>
                        <Text sx={{ fontSize: rem(20) }}>🥇🥈🥉</Text>
                        <Text size="sm" color="dimmed">
                            Top 3 winners receive a pair of tickets to any stand-up show (max $70 face value/ticket)
                        </Text>
                    </div>

                    <Divider my="md" />
                    <Center>
                        <Stack spacing={2} align="center">
                            <Text size="xs" color="dimmed">Presented by</Text>
                            <Image src={logo} maw={80} fit="contain" />
                        </Stack>
                    </Center>
                </Card>
            </Container>

            {/* ═══════════════════════════════════════
                HOW TO EARN POINTS
            ════════════════════════════════════════ */}
            <SectionDivider label="How to earn points" />

            <Container size="lg">
                <Group mb="md" spacing="xs" position="center">
                    <IconHelpCircle size={22} color={TEAL} />
                    <Text className={classes.sectionTitle}>How To Earn Points</Text>
                </Group>

                <SimpleGrid
                    cols={3}
                    spacing="lg"
                    breakpoints={[
                        { maxWidth: '62rem', cols: 2, spacing: 'md' },
                        { maxWidth: '36rem', cols: 1, spacing: 'sm' },
                    ]}
                >
                    {/* Comedians & Fans */}
                    <Card className={classes.pointsCard} padding="lg" radius="lg" withBorder shadow="sm">
                        <Text className={classes.pointsCardTitle} sx={{ color: PURPLE }}>
                            Comedians &amp; Fans
                        </Text>
                        <PointRow value="+1" description="Submit a valid paid or free showcase" />
                        <PointRow value="+2" description="Submit a valid open mic" />
                        <PointRow value="+3" description="Report a valid dead mic" />
                        <PointRow value="+5" description="Rank your first comedian on your sense of humor profile" />
                        <PointRow value="+10" description="Report a valid bug" />
                    </Card>

                    {/* Comedians only */}
                    <Card className={classes.pointsCard} padding="lg" radius="lg" withBorder shadow="sm"
                        sx={{ borderTop: `3px solid ${TEAL}` }}
                    >
                        <Text className={classes.pointsCardTitle} sx={{ color: TEAL }}>
                            Comedians Only
                        </Text>
                        <PointRow value="+1" description="Check into an open mic or showcase" />
                        <PointRow value="+1" description="Verified on stage by a fan or comic" />
                        <PointRow value="+2" description="Receive an upvote from a fan" />
                        <PointRow value="+10" description="Receive an upvote from a new fan" />
                    </Card>

                    {/* Fans only */}
                    <Card className={classes.pointsCard} padding="lg" radius="lg" withBorder shadow="sm"
                        sx={{ borderTop: `3px solid ${GREEN}` }}
                    >
                        <Text className={classes.pointsCardTitle} sx={{ color: GREEN }}>
                            Fans Only
                        </Text>
                        <PointRow value="+1" description="Buy tickets to a showcase" />
                        <PointRow value="+1" description="Complete post-show verification and upvoting" />
                        <PointRow value="+10" description="Check into an open mic" />
                    </Card>
                </SimpleGrid>
            </Container>

            <Space h="xl" />
            <Footer links={links} />
        </body>
    )
}

export default LeaderboardPrizes26